import { useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft as iconBack } from "@fortawesome/pro-thin-svg-icons";
import { faBars as iconMenu } from "@fortawesome/pro-thin-svg-icons";
import { faChevronDown as iconChevronDown } from "@fortawesome/pro-thin-svg-icons";
import { faChevronUp as iconChevronUp } from "@fortawesome/pro-thin-svg-icons";
import { faPen as iconEdit } from "@fortawesome/pro-thin-svg-icons";
import { faRefresh as iconRefresh } from "@fortawesome/pro-thin-svg-icons";
import { faTrash as iconRemove } from "@fortawesome/pro-thin-svg-icons";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import configEntzy from "components/config/ConfigEntzy";
import { MoreButton } from "components/utils/common/CommonButtons";

function renderConversationPreview(conversation) {
  const lastMessage = conversation.messages[conversation.messages.length - 1];

  if (!lastMessage) {
    return "No messages yet";
  }

  return lastMessage.body || "Encrypted message ready";
}

function CnxChatRoom({
  panelSx,
  subtlePanelSx,
  fieldSx,
  activeView,
  recipientPointer,
  setRecipientPointer,
  memberPointer,
  setMemberPointer,
  messageBody,
  setMessageBody,
  selectedConversationKey,
  isActionLoading,
  handleCreateConversation,
  handleAddParticipant,
  handleRemoveParticipant,
  handleSend,
  handleSyncThread,
  conversationTitleDraft,
  setConversationTitleDraft,
  isEditingConversationTitle,
  handleStartConversationTitleEdit,
  handleCancelConversationTitleEdit,
  handleSaveConversationTitle,
  conversations,
  selectedConversation,
  setSelectedConversationId,
  isConversationListLoading,
}) {
  const [roomMenuOpen, setRoomMenuOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [cryptoOpen, setCryptoOpen] = useState(false);
  const selectedConversationMessages = selectedConversation?.messages || [];
  const selectedConversationParticipantCount =
    selectedConversation?.participantCount || 0;
  const canSendMessages = selectedConversationParticipantCount >= 2;

  if (activeView === "newChat") {
    return (
      <Box sx={{ ...panelSx, minHeight: 560 }}>
        <Box sx={{ maxWidth: 620, mx: "auto", width: "100%" }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Create a new room
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.72 }}>
            Create the room container first with a simple default setup. Member
            lookup, room settings, and invite fallbacks can then happen inside the
            live room.
          </Typography>
          <Stack spacing={1.5} sx={{ mt: configEntzy.APP_SPACING_MD }}>
            <TextField
              fullWidth
              label="Room title (optional)"
              placeholder="Leave blank to use Chat DD MMM"
              value={recipientPointer}
              sx={fieldSx}
              onChange={(event) => setRecipientPointer(event.target.value)}
            />
            <MoreButton
              variant="contained"
              size="large"
              type="button"
              color="bright"
              className="cnx-exp-primary-button"
              disabled={isActionLoading("createConversation")}
              text={
                isActionLoading("createConversation")
                  ? "Creating..."
                  : "Create Room"
              }
              onClick={handleCreateConversation}
            />
          </Stack>
        </Box>
      </Box>
    );
  }

  if (activeView === "settings") {
    return (
      <Box sx={{ ...panelSx, minHeight: 560 }}>
        <Box sx={{ maxWidth: 680, mx: "auto", width: "100%" }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Chat settings
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.72 }}>
            Provider-backed session details, environment info, and proof diagnostics
            now live in the debug drawer so this surface can stay minimal.
          </Typography>
          <Box sx={{ ...subtlePanelSx, mt: configEntzy.APP_SPACING_MD }}>
            <Typography variant="body2" sx={{ opacity: 0.74 }}>
              Settings will grow here as the CNX chat experience matures. For now,
              use the top navigation to refresh the session, disconnect, or open
              provider diagnostics.
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  if (selectedConversation) {
    return (
      <Box sx={{ ...panelSx, minHeight: 560, p: 0, overflow: "hidden" }}>
        <Box
          sx={{
            px: configEntzy.APP_SPACING_MD,
            py: configEntzy.APP_SPACING_MD,
            borderBottom: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "56px minmax(0, 1fr) 56px",
              alignItems: "center",
              gap: 1,
            }}
          >
            <IconButton
              size="small"
              sx={{ color: "#eef2ff", justifySelf: "start" }}
              onClick={() => setSelectedConversationId("")}
            >
              <FontAwesomeIcon icon={iconBack} />
            </IconButton>
            <Box sx={{ minWidth: 0, textAlign: "center", position: "relative" }}>
              {isEditingConversationTitle ? (
                <Box sx={{ maxWidth: 420, mx: "auto" }}>
                  <TextField
                    fullWidth
                    label="Room title"
                    placeholder="Untitled chat"
                    value={conversationTitleDraft}
                    sx={{
                      ...fieldSx,
                      "& .MuiInputBase-root": {
                        fontSize: "1rem",
                        fontWeight: 700,
                      },
                    }}
                    onChange={(event) => setConversationTitleDraft(event.target.value)}
                  />
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mt: 1, justifyContent: "center" }}
                  >
                    <MoreButton
                      variant="contained"
                      size="small"
                      type="button"
                      color="bright"
                      className="cnx-exp-primary-button"
                      disabled={isActionLoading("updateConversation")}
                      text={
                        isActionLoading("updateConversation")
                          ? "Saving..."
                          : "Save"
                      }
                      onClick={handleSaveConversationTitle}
                    />
                    <MoreButton
                      variant="contained"
                      size="small"
                      type="button"
                      color="bright"
                      className="cnx-exp-nav-button"
                      onClick={handleCancelConversationTitleEdit}
                      text="Cancel"
                    />
                  </Stack>
                </Box>
              ) : (
                <>
                  <Typography variant="h6" sx={{ fontWeight: 700, px: 4 }}>
                    {selectedConversation.title || "Untitled chat"}
                  </Typography>
                  <IconButton
                    size="small"
                    sx={{
                      position: "absolute",
                      right: 0,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "rgba(238,242,255,0.72)",
                    }}
                    onClick={handleStartConversationTitleEdit}
                  >
                    <FontAwesomeIcon icon={iconEdit} />
                  </IconButton>
                </>
              )}
              <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.68 }}>
                {selectedConversation.recipientPointer || "No members added yet"}
              </Typography>
            </Box>
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ justifySelf: "end", alignItems: "center", width: 56 }}
            >
              <IconButton
                size="small"
                sx={{ color: "#eef2ff" }}
                disabled={isActionLoading("syncThread")}
                onClick={handleSyncThread}
              >
                <FontAwesomeIcon icon={iconRefresh} spin={isActionLoading("syncThread")} />
              </IconButton>
              <IconButton
                size="small"
                sx={{ color: "#eef2ff" }}
                onClick={() => setRoomMenuOpen(true)}
              >
                <FontAwesomeIcon icon={iconMenu} />
              </IconButton>
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            px: configEntzy.APP_SPACING_MD,
            py: configEntzy.APP_SPACING_MD,
            minHeight: 360,
            maxHeight: { xs: "none", lg: 420 },
            overflowY: "auto",
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.012), rgba(255,255,255,0.02))",
          }}
        >
          {selectedConversationMessages.length ? (
            <Box className="cnx-exp-message-stack">
              {selectedConversationMessages.map((message) => (
                <Box
                  key={message.id}
                  sx={{
                    alignSelf:
                      message.senderRole === "me" ? "flex-end" : "flex-start",
                    width: { xs: "100%", sm: "auto" },
                    maxWidth: { sm: "70%" },
                    px: 1.75,
                    py: 1.4,
                    borderRadius: 2.5,
                    border: "1px solid rgba(255,255,255,0.05)",
                    background:
                      message.senderRole === "me"
                        ? "linear-gradient(180deg, rgba(255,159,90,0.18), rgba(24,27,38,0.98))"
                        : "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(16,18,27,0.98))",
                  }}
                >
                  <Typography variant="body1">{message.body}</Typography>
                  <Typography
                    variant="caption"
                    sx={{ mt: 0.75, display: "block", opacity: 0.58 }}
                  >
                    {message.timestamp} · {message.deliveryState}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ ...subtlePanelSx, p: configEntzy.APP_SPACING_MD }}>
              <Typography variant="body2" sx={{ opacity: 0.72 }}>
                This conversation is ready. The first encrypted message will appear
                here when it arrives.
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            px: configEntzy.APP_SPACING_MD,
            py: configEntzy.APP_SPACING_MD,
            borderTop: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <Stack spacing={1.1}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Message draft"
              placeholder="Write your message. Encryption happens locally before send."
              value={messageBody}
              sx={fieldSx}
              onChange={(event) => setMessageBody(event.target.value)}
            />
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.25}
              sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}
            >
              <Typography variant="body2" sx={{ opacity: 0.64 }}>
                {canSendMessages
                  ? "Plaintext stays local. The provider only sends the encrypted envelope."
                  : "This room is still owner-only. Add one member before sending the first encrypted message."}
              </Typography>
              <MoreButton
                variant="contained"
                size="medium"
                type="button"
                color="bright"
                className="cnx-exp-primary-button"
                disabled={!canSendMessages || isActionLoading("sendMessage")}
                text={isActionLoading("sendMessage") ? "Sending..." : "Send"}
                onClick={handleSend}
              />
            </Stack>
          </Stack>
        </Box>

        <Drawer
          anchor="right"
          open={roomMenuOpen}
          onClose={() => setRoomMenuOpen(false)}
          PaperProps={{
            sx: {
              width: { xs: "100%", sm: 380 },
              background:
                "linear-gradient(180deg, rgba(15,18,26,0.99), rgba(9,11,17,0.99))",
              color: "#eef2ff",
              borderLeft: "1px solid rgba(255,255,255,0.06)",
              p: 2,
            },
          }}
        >
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="body2" sx={{ opacity: 0.64 }}>
                Room menu
              </Typography>
              <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 700 }}>
                {selectedConversation.title || "Untitled chat"}
              </Typography>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box sx={{ ...subtlePanelSx, p: 1.5 }}>
              <Stack
                direction="row"
                sx={{ alignItems: "center", justifyContent: "space-between" }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Members
                </Typography>
                <IconButton
                  size="small"
                  sx={{ color: "#eef2ff" }}
                  onClick={() => setMembersOpen((current) => !current)}
                >
                  <FontAwesomeIcon
                    icon={membersOpen ? iconChevronUp : iconChevronDown}
                  />
                </IconButton>
              </Stack>
              <Collapse in={membersOpen}>
                <Stack spacing={1} sx={{ mt: 1.25 }}>
                  {selectedConversation.participants?.length ? (
                    selectedConversation.participants.map((participant) => (
                      <Box
                        key={participant.id}
                        sx={{
                          px: 1.25,
                          py: 1,
                          borderRadius: 2.5,
                          border: "1px solid rgba(255,255,255,0.06)",
                          background: "rgba(255,255,255,0.02)",
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, overflowWrap: "anywhere" }}
                            >
                              {participant.pointer || participant.label}
                            </Typography>
                            {participant.name ? (
                              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                {participant.name}
                              </Typography>
                            ) : null}
                            <Typography variant="caption" sx={{ display: "block", opacity: 0.58 }}>
                              {participant.role || "member"}
                            </Typography>
                          </Box>
                          {participant.role !== "owner" ? (
                            <IconButton
                              size="small"
                              sx={{ color: "#ffb588" }}
                              disabled={
                                !participant.id || isActionLoading("removeParticipants")
                              }
                              onClick={() => handleRemoveParticipant(participant)}
                            >
                              <FontAwesomeIcon icon={iconRemove} />
                            </IconButton>
                          ) : null}
                        </Stack>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" sx={{ opacity: 0.62 }}>
                      Member names will appear here once the provider returns the participant list.
                    </Typography>
                  )}
                </Stack>
              </Collapse>
            </Box>

            <Box sx={{ ...subtlePanelSx, p: 1.5 }}>
              <Stack
                direction="row"
                sx={{ alignItems: "center", justifyContent: "space-between" }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Add member
                </Typography>
                <IconButton
                  size="small"
                  sx={{ color: "#eef2ff" }}
                  onClick={() => setAddMemberOpen((current) => !current)}
                >
                  <FontAwesomeIcon
                    icon={addMemberOpen ? iconChevronUp : iconChevronDown}
                  />
                </IconButton>
              </Stack>
              <Collapse in={addMemberOpen}>
                <Stack spacing={1} sx={{ mt: 1.25 }}>
                  <TextField
                    fullWidth
                    label="Add member by CNX pointer"
                    placeholder="::name or @name"
                    value={memberPointer}
                    sx={fieldSx}
                    onChange={(event) => setMemberPointer(event.target.value)}
                  />
                  <MoreButton
                    variant="contained"
                    size="small"
                    type="button"
                    color="bright"
                    className="cnx-exp-primary-button"
                    disabled={isActionLoading("addParticipants")}
                    text={isActionLoading("addParticipants") ? "Adding..." : "Add Member"}
                    onClick={handleAddParticipant}
                  />
                </Stack>
              </Collapse>
            </Box>

            <Box sx={{ ...subtlePanelSx, p: 1.5 }}>
              <Stack
                direction="row"
                sx={{ alignItems: "center", justifyContent: "space-between" }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Encryption
                </Typography>
                <IconButton
                  size="small"
                  sx={{ color: "#eef2ff" }}
                  onClick={() => setCryptoOpen((current) => !current)}
                >
                  <FontAwesomeIcon
                    icon={cryptoOpen ? iconChevronUp : iconChevronDown}
                  />
                </IconButton>
              </Stack>
              <Collapse in={cryptoOpen}>
                <Stack spacing={1} sx={{ mt: 1.25 }}>
                  <Typography variant="body2" sx={{ opacity: 0.82 }}>
                    {selectedConversationKey
                      ? "A local proof room key is already available on this browser for this conversation."
                      : "A local proof room key will be generated automatically on the first send from this browser."}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.64 }}>
                    Raw key import and envelope diagnostics live in the debug drawer while wrapped
                    conversation-key distribution is still pending.
                  </Typography>
                </Stack>
              </Collapse>
            </Box>
          </Stack>
        </Drawer>
      </Box>
    );
  }

  return (
    <Box sx={{ ...panelSx, minHeight: 560 }}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Conversations
      </Typography>
      <Typography variant="body2" sx={{ mt: 1, opacity: 0.72 }}>
        Your chat list lives here. Open a new chat from the top navigation when
        you are ready.
      </Typography>

      <Stack spacing={1.1} sx={{ mt: configEntzy.APP_SPACING_MD }}>
        {conversations.length ? (
          conversations.map((conversation) => (
            <Box
              key={conversation.id}
              sx={{
                ...subtlePanelSx,
                p: configEntzy.APP_SPACING_MD,
                cursor: "pointer",
                transition: "background 140ms ease, border-color 140ms ease",
                "&:hover": {
                  borderColor: "rgba(255,159,90,0.24)",
                  background:
                    "linear-gradient(180deg, rgba(255,159,90,0.08), rgba(16,18,27,0.98))",
                },
              }}
              onClick={() => setSelectedConversationId(conversation.id)}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: "center", justifyContent: "space-between" }}
              >
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {conversation.title}
                </Typography>
                {conversation.unreadCount ? (
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    {conversation.unreadCount} new
                  </Typography>
                ) : null}
              </Stack>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.75,
                  opacity: 0.68,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {renderConversationPreview(conversation)}
              </Typography>
            </Box>
          ))
        ) : isConversationListLoading ? (
          <Box
            sx={{
              minHeight: 380,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              px: configEntzy.APP_SPACING_LG,
            }}
          >
            <Box>
              <CircularProgress
                size={28}
                thickness={5}
                sx={{ color: "#ff9f5a" }}
              />
              <Typography variant="body2" sx={{ mt: 1.5, opacity: 0.74 }}>
                Loading your chat rooms...
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              minHeight: 380,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              px: configEntzy.APP_SPACING_LG,
            }}
          >
            <Box>
              <Typography
                variant="body2"
                sx={{
                  display: "inline-flex",
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.08)",
                  background: "rgba(255,255,255,0.03)",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  opacity: 0.76,
                }}
              >
                0 conversations
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.72 }}>
                Your CNX session is ready. Start a new chat when you want to open
                the first encrypted thread.
              </Typography>
            </Box>
          </Box>
        )}
      </Stack>
    </Box>
  );
}

export default CnxChatRoom;
